import React, { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { Button, Modal, Input, Radio, Tooltip, Cascader, Select } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getWavenumbers } from '../utils/wavenumbers';
import { mapState } from '../stateManagement/commonState';
import { substanceArray } from '../utils/substancesAndColors';

let type = 'processed'

const DownloadCSV = ({ deviceInfo, currentSerial }) => {
    const [map] = useAtom(mapState);
    const wavenumbers = deviceInfo ? getWavenumbers(500, deviceInfo.wavelength_calibration, deviceInfo.excitation_wavelength) : [];
    const standardWavenumbers = [...Array(2324 - 225 + 1)].map((_, i) => 225 + i);
    const [cascaderOptions, setCascaderOptions] = useState([]);
    const [labelsToExclude, setLabelsToExclude] = useState([]);
    const [mlLabelsToInclude, setMlLabelsToInclude] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const mlLabelOptions = substanceArray.map(label => ({ label, value: label }));

    useEffect(() => {
        if (map.length) {
            const uniqueLabels = [...new Set(map.map(e => e.label))];
            setCascaderOptions(uniqueLabels.map(label => ({ label, value: label })));
        }
    }, [map]);

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const downloadCSV = labelsToExclude => {
        const wavenumberHeader = type === 'processed' ? standardWavenumbers : wavenumbers;
        const signalHeader = ['autocorrelationWidth', 'lowFrequencyEnergy', 'signalToNoise', 'reconstructionError'];
        const mlHeader = mlLabelsToInclude.flatMap(label => [`${label} (avg)`, `${label} (ML1)`, `${label} (ML2)`]);
        const header = [null, ...signalHeader, ...mlHeader, ...wavenumberHeader];
        const getSignalValues = e => [e.autocorrelationWidth ?? '', e.lowFrequencyEnergy ?? '', e.signalToNoise ?? '', e.reconstructionError ?? ''];
        const getMlValues = mlOutput => mlLabelsToInclude.flatMap(label => {
            const entry = mlOutput?.find(row => row[0] === label);
            return entry ? [entry[1], entry[2], entry[3]] : ['', '', ''];
        });
        let spectra;
        const filteredMap = map.filter(e => !labelsToExclude.includes(e.label));
        if (type === 'processed') {
            spectra = filteredMap.map(e => [e.label, ...getSignalValues(e), ...getMlValues(e.mlOutput), ...e.processedSpectrum]);
        } else if (type === 'raw') {
            spectra = filteredMap.map(e => [e.label, ...getSignalValues(e), ...getMlValues(e.mlOutput), ...e.rawSpectrum]);
        }
        let csv = [header, ...spectra].map(row => row.join(',')).join('\n');
        let blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        let url = URL.createObjectURL(blob);

        let a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', `Scatr Map Serial ${currentSerial} - ${type}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setIsModalVisible(false);
    }

    const filter = (inputValue, path) => path.some((option) => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

    return (
        <>
            <Tooltip placement="bottomRight" title={`Download Map`}>
                <Button icon={<DownloadOutlined />} onClick={showModal} disabled={map.length ? false : true}></Button>
                <Modal title='Download' open={isModalVisible} onOk={() => { downloadCSV(labelsToExclude) }} onCancel={handleCancel}>
                    <div>Serial: {currentSerial}</div>
                    <div>Exclude Labels:</div>
                    <Cascader
                        multiple
                        maxTagCount='responsive'
                        style={{ width: '100%', margin: '10px 0' }}
                        options={cascaderOptions}
                        onChange={e => setLabelsToExclude(e.flat())}
                        placeholder="Add labels to exclude"
                        showSearch={{ filter }}
                    />
                    <div>Include ML Output:</div>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        maxTagCount='responsive'
                        style={{ width: '100%', margin: '10px 0' }}
                        options={mlLabelOptions}
                        value={mlLabelsToInclude}
                        onChange={setMlLabelsToInclude}
                        placeholder="Add ML labels to include"
                        optionFilterProp="label"
                        filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                    />
                    <Radio.Group defaultValue={type} buttonStyle="solid" onChange={e => type = e.target.value}>
                        <Radio.Button value="raw">Raw</Radio.Button>
                        <Radio.Button value="processed">Processed</Radio.Button>
                    </Radio.Group>
                </Modal>
            </Tooltip>
        </>
    )
};

export default DownloadCSV;